import { useColorMode } from "@chakra-ui/react";
import {
  blue,
  chartTheme,
  ColorMode,
  fonts,
  magenta,
  palette,
  seriesPalette,
} from "@intentsify/brand";
import type { ChartTheme } from "@tanstack/charts";
import chroma from "chroma-js";
import { CSSProperties } from "react";
import { AnyColour, Colour, parseModifiableColour } from "theme";

export type BrandChartTheme = {
  mode: ColorMode;
  /** Passed as `theme` in a TanStack chart definition. */
  theme: ChartTheme;
  /** Primary single-series colour. */
  primary: string;
  /** CSS variables and typography for the chart container. */
  containerStyle: CSSProperties;
};

export const getBrandChartTheme = (mode: ColorMode): BrandChartTheme => {
  const chrome = chartTheme[mode];
  const palette = seriesPalette[mode];

  return {
    mode,
    theme: {
      foreground: chrome.foreground,
      muted: chrome.muted,
      grid: chrome.grid,
      background: "transparent",
      palette,
    },
    primary: palette[0],
    containerStyle: {
      color: chrome.foreground,
      fontFamily: fonts.stack,
      ["--ts-chart-tooltip-background" as string]: chrome.tooltipBackground,
      ["--ts-chart-tooltip-color" as string]: chrome.foreground,
      ["--ts-chart-tooltip-border" as string]: `1px solid ${chrome.tooltipBorder}`,
      ["--ts-chart-tooltip-border-radius" as string]: "8px",
      ["--ts-chart-tooltip-shadow" as string]:
        "0 8px 24px rgba(17, 21, 20, 0.12)",
      ["--ts-chart-tooltip-font" as string]: `14px ${fonts.stack}`,
    },
  };
};

export const useBrandChartTheme = (): BrandChartTheme => {
  const { colorMode } = useColorMode();

  return getBrandChartTheme(colorMode);
};

const namedColours: Record<Colour, Record<ColorMode, string>> = {
  green: { light: seriesPalette.light[0], dark: seriesPalette.dark[0] },
  blue: { light: palette.blue, dark: palette.blue },
  yellow: { light: palette.gold, dark: palette.gold },
  teal: { light: palette.teal, dark: blue[200] },
  orange: { light: palette.orange, dark: palette.orange },
  pink: { light: palette.magenta, dark: magenta[300] },
  red: { light: palette.red, dark: palette.red },
};

/**
 * Brand colour for a portal colour name (`"green"`, `"blue.100"`). A shade
 * modifier lightens the colour, as the legacy palettes did.
 */
export const brandColourFor = (colour: AnyColour, mode: ColorMode) => {
  const [name, modifier] = parseModifiableColour(colour);
  const base = namedColours[name][mode];

  return modifier > 0
    ? chroma
        .mix(base, mode === "light" ? palette.white : palette.night, 0.45)
        .hex()
    : base;
};
